// /web/src/pages/calculators/Risers.jsx
import React from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useApi } from "../../lib/api";
import { trackCalculatorRun } from "../../lib/amplitude";
import { toast } from "react-hot-toast";
import useUsageEvent from "../../hooks/useUsageEvent";
import CalculatorLayout, {
  CalculatorSection,
} from "../../components/calculators/CalculatorLayout.jsx";

const RISER_CALCULATOR_META = {
  title: "Riser schedule calculator",
  subtitle: "Choose a ventilation basis and add rainwater to generate a coordinated riser envelope.",
  notes: [
    "Ventilation loads follow Building Regulations / CIBSE Guide B or the BCO 2023 allowances—choose the one that matches your basis of design.",
    "Each bank reserves depth for supply, extract, drainage, comms, and a 10% spare allowance so the footprint can be coordinated with structure.",
    "Enable rainwater when the vertical zone needs to carry roof drainage; disable it if rainwater drops elsewhere in the core.",
  ],
};

function toErrorMessage(err) {
  if (!err) return "Unknown error";
  if (typeof err === "string") return err;
  if (err.detail) {
    if (typeof err.detail === "string") return err.detail;
    if (Array.isArray(err.detail) && err.detail[0]?.msg) return err.detail[0].msg;
  }
  if (typeof err.message === "string" && err.message !== "[object Object]") return err.message;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

export default function RiserSchedule() {
  const api = useApi();
  const params = useParams();
  const [sp] = useSearchParams();
  const projectId =
    params.id ||
    sp.get("project_id") ||
    sp.get("projectId") ||
    sp.get("project") ||
    null;
  const rawUsageEventId = sp.get("usage_event_id");
  const { event: historyEvent, loading: historyLoading, error: historyError, usageEventId } =
    useUsageEvent(rawUsageEventId);

  const [vent, setVent] = React.useState("br_cibse"); // "br_cibse" | "bco_2023"
  const [includeRain, setIncludeRain] = React.useState(true);
  const [result, setResult] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [project, setProject] = React.useState(null);
  const [projectLoading, setProjectLoading] = React.useState(!!projectId);
  const [loadError, setLoadError] = React.useState("");
  const [projects, setProjects] = React.useState([]);
  const [loadingProjects, setLoadingProjects] = React.useState(!projectId);
  const [pickErr, setPickErr] = React.useState("");

  React.useEffect(() => {
    let active = true;

    async function loadProject(pid) {
      try {
        setProjectLoading(true);
        setLoadError("");
        setResult(null);
        setErr("");
        const proj = await api(`/projects/${pid}`);
        if (!active) return;
        setProject(proj || null);
      } catch (e) {
        if (!active) return;
        setProject(null);
        setLoadError(toErrorMessage(e) || "Failed to load project");
      } finally {
        if (active) setProjectLoading(false);
      }
    }

    async function loadProjectsList() {
      try {
        setLoadingProjects(true);
        setPickErr("");
        const list = await api(`/projects`);
        if (!active) return;
        setProjects(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!active) return;
        setPickErr(toErrorMessage(e) || "Failed to load projects");
      } finally {
        if (active) setLoadingProjects(false);
      }
    }

    if (projectId) {
      setProjects([]);
      setLoadingProjects(false);
      setPickErr("");
      loadProject(projectId);
    } else if (!usageEventId) {
      setProject(null);
      setProjectLoading(false);
      setLoadError("");
      setResult(null);
      setErr("");
      loadProjectsList();
    } else {
      setProject(null);
      setProjectLoading(false);
      setLoadError("");
      setResult(null);
      setErr("");
      setLoadingProjects(false);
      setProjects([]);
      setPickErr("");
    }

    return () => {
      active = false;
    };
  }, [projectId, usageEventId, api]);

  React.useEffect(() => {
    if (!usageEventId || !historyEvent) return;
    const details = historyEvent.details || {};
    if (typeof details.vent === "string") {
      setVent(details.vent === "bco_2023" ? "bco_2023" : "br_cibse");
    }
    if (typeof details.include_rainwater === "boolean") {
      setIncludeRain(details.include_rainwater);
    }
    if (historyEvent.result_json) {
      setResult(historyEvent.result_json);
    }
  }, [usageEventId, historyEvent]);

  async function run() {
    if (!projectId) {
      const message = "Missing project selection";
      setErr(message);
      toast.error(message);
      return;
    }
    setLoading(true); setErr(""); setResult(null);
    try{
      const qs = new URLSearchParams({ project_id: projectId, vent, include_rainwater: String(includeRain) });
      const res = await api(`/calc/risers/schedule?${qs.toString()}`, { method: "GET" });
      const totalArea = Number(res?.total_combined_area_m2);
      trackCalculatorRun("risers", {
        projectId,
        inputs: { vent, include_rainwater: includeRain },
        resultSummary: {
          banks: Array.isArray(res?.banks) ? res.banks.length : undefined,
          total_area_m2: Number.isFinite(totalArea) ? totalArea : undefined,
          include_rainwater: includeRain,
        },
      });
      setResult(res);
      toast.success("Calculation complete");
    }catch(e){
      const message = toErrorMessage(e) || "Run failed";
      setErr(message);
      toast.error(message);
    }
    finally{ setLoading(false); }
  }

  const showPicker = !projectId && !usageEventId;
  const projectSummary = project
    ? [
        project.name,
        project.postcode,
        project.local_authority,
        project.floors_count ? `${project.floors_count} floors` : null,
      ].filter(Boolean)
    : null;

  const historyProject = historyEvent?.project_id
    ? { id: historyEvent.project_id, name: historyEvent.project_name }
    : null;
  const layoutProject = project || historyProject;
  const historySummary = historyEvent
    ? [
        historyEvent.project_name || "Saved run",
        historyEvent.created_at
          ? new Date(historyEvent.created_at).toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : null,
      ].filter(Boolean)
    : null;
  const layoutDetails = project ? projectSummary : historySummary;
  const historyBanner = (() => {
    if (!usageEventId) return null;
    if (historyLoading) return "Loading saved calculator run…";
    if (historyError) return `Failed to load saved run: ${historyError}`;
    if (historyEvent) {
      const ts = historyEvent.created_at
        ? new Date(historyEvent.created_at).toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
          })
        : null;
      return ts
        ? `Restored calculator inputs from ${ts}. Run again to refresh the schedule.`
        : "Restored calculator inputs from your history.";
    }
    return null;
  })();

  return (
    <CalculatorLayout
      title={RISER_CALCULATOR_META.title}
      project={layoutProject}
      projectDetails={layoutDetails}
      onRun={showPicker ? null : run}
      runLabel="Generate schedule"
      runDisabled={showPicker || loading || projectLoading}
      runLoading={loading}
      subtitle={showPicker ? RISER_CALCULATOR_META.subtitle : undefined}
    >
      {usageEventId && historyBanner && (
        <div className="pd-inline-alert pd-inline-alert--info">{historyBanner}</div>
      )}

      {showPicker ? (
        <CalculatorSection title="Select a project" description="Pick a project to run the riser calculator.">
          {pickErr && (
            <div className="pd-alert">
              <strong>Error:</strong> {pickErr}
            </div>
          )}
          {loadingProjects ? (
            <div className="pd-results-placeholder pd-results-placeholder--auto">
              Loading your projects…
            </div>
          ) : projects.length === 0 ? (
            <div className="pd-empty-state">
              <p>You don’t have any projects yet.</p>
              <div className="pd-empty-state__actions">
                <Link to="/projects/new" className="pd-btn pd-btn--primary">
                  + Create new project
                </Link>
              </div>
            </div>
          ) : (
            <div className="pd-project-grid">
              {projects.map((p) => (
                <Link
                  key={p.id}
                  to={`/projects/${p.id}/calculators/risers`}
                  className="pd-project-card"
                >
                  <div className="pd-project-card__head">
                    <div>
                      <div className="pd-project-card__title">{p.name}</div>
                      <div className="pd-project-card__meta">
                        {p.postcode || "—"} · {p.local_authority || "—"}
                      </div>
                    </div>
                    <span className="pd-pill">{(p.building_use || "office").toUpperCase()}</span>
                  </div>
                  <div className="pd-project-card__foot">
                    Floors: {p.floors_count ?? "—"}
                    <span className="pd-project-card__cta">Open calculator</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CalculatorSection>
      ) : (
        <>
          {projectLoading && <div className="pd-caption">Loading project…</div>}
          {loadError && (
            <div className="pd-alert">
              <strong>Error:</strong> {loadError}
            </div>
          )}

          <CalculatorSection
            title="Input parameters"
            description="Select your ventilation basis and whether to include rainwater services."
            actions={
              projectId ? (
                <Link to={`/projects/${projectId}`} className="pd-btn pd-btn--ghost">
                  Back to project
                </Link>
              ) : null
            }
          >
            <div className="pd-input-grid">
              <div className="pd-form-group">
                <label htmlFor="riser-vent-standard">Ventilation standard</label>
                <select
                  id="riser-vent-standard"
                  value={vent}
                  onChange={(e) => setVent(e.target.value)}
                >
                  <option value="br_cibse">Building Regs / CIBSE (10 L/s·person or 1 L/s·m²)</option>
                  <option value="bco_2023">BCO 2023 (14 L/s·person)</option>
                </select>
                <p className="pd-form-note">Align this with the mechanical design basis.</p>
              </div>
              <div className="pd-form-group">
                <label htmlFor="riser-include-rainwater">Include rainwater stacks</label>
                <select
                  id="riser-include-rainwater"
                  value={includeRain ? "yes" : "no"}
                  onChange={(e) => setIncludeRain(e.target.value === "yes")}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
                <p className="pd-form-note">Toggle off if rainwater drops elsewhere in the core.</p>
              </div>
            </div>
            {err && (
              <div className="pd-alert">
                <strong>Error:</strong> {err}
              </div>
            )}
          </CalculatorSection>

          <CalculatorSection
            title="Notes"
            description="Key assumptions for the riser calculator."
          >
            <div className="pd-calculator__notes">
              <ul>
                {RISER_CALCULATOR_META.notes.map((note, idx) => (
                  <li key={idx}>{note}</li>
                ))}
              </ul>
            </div>
          </CalculatorSection>

          <CalculatorSection title="Results" description="Run the schedule to see recommended riser banks and footprints.">
            {result && Array.isArray(result.banks) && result.banks.length > 0 ? (
              <div className="pd-results-card">
                <div>
                  <div className="pd-panel__title">Bank-by-bank schedule</div>
                  <p className="pd-results-card__meta">
                    {vent === "br_cibse"
                      ? "Building Regs / CIBSE basis"
                      : "BCO 2023 basis"}
                    {includeRain ? " · Includes rainwater services" : " · No rainwater services"}
                  </p>
                </div>
                <div className="pd-table-wrapper">
                  <table className="pd-table">
                    <thead>
                      <tr>
                        <th scope="col">Riser bank</th>
                        <th scope="col">Clear opening (mm)</th>
                        <th scope="col">Area (m²)</th>
                        <th scope="col">Items</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.banks.map((b, i) => {
                        const area = (b.clear_w_mm / 1000) * (b.clear_d_mm / 1000);
                        return (
                          <tr key={i}>
                            <td>{b.name}</td>
                            <td>
                              {b.clear_w_mm} × {b.clear_d_mm}
                            </td>
                            <td>{area.toFixed(3)}</td>
                            <td>
                              <ul className="riser-calculator__items-list">
                                {b.items.map((it, j) => (
                                  <li key={j} className="riser-calculator__item">
                                    {it.kind}
                                    {it.count ? ` ×${it.count}` : ""}
                                    {it.w_mm && it.d_mm ? ` — ${it.w_mm}×${it.d_mm} mm` : ""}
                                  </li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="pd-summary-note">
                  <div className="pd-summary-note__meta">Total combined area</div>
                  {Number(result.total_combined_area_m2).toFixed(3)} m²
                </div>
                {result.notes?.length > 0 && (
                  <div className="pd-calculator__result-note">
                    <strong>Result notes</strong>
                    <ul className="pd-notes-list">
                      {result.notes.map((note, idx) => (
                        <li key={idx}>{note}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="pd-results-placeholder">Run the calculator to generate a schedule.</div>
            )}
          </CalculatorSection>
        </>
      )}
    </CalculatorLayout>
  );
}
